import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button, CircularProgress, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';

import { getCategoryFromPathname } from '../utils/analytics/navigation';
import { trackSentryError } from '../utils/analytics/sentry';
import { isInRedirectFlow, registerAsync } from '../utils/auth';
import { useFirebaseLoginState } from '../utils/hooks';

const RegisterScreen: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isRegistered, isAuthStateLoading } = useFirebaseLoginState();
  const location = useLocation();

  const handleRegister = async () => {
    try {
      setError(null);
      setIsRedirecting(true);
      const category = getCategoryFromPathname(location.pathname);
      await registerAsync(category);
      // Note: User will be redirected to Google auth, so we don't navigate here
      // The success handling happens in App.tsx when user returns from redirect
    } catch (error) {
      trackSentryError('RegisterScreen: Registration error:', { error });
      console.error('RegisterScreen: Registration error:', error);
      setError(t('app.errors.registrationFailed'));
      setIsRedirecting(false);
    }
  };

  useEffect(() => {
    if (isRegistered) {
      navigate(`/app/campaigns${location.search}`);
      return;
    }

    if (isInRedirectFlow()) {
      // Show loading while redirect result is being processed
      return;
    }

    if (!isAuthStateLoading && !isInRedirectFlow()) {
      handleRegister();
    }
  }, [isRegistered, isAuthStateLoading]);

  if (isInRedirectFlow() || isAuthStateLoading) {
    return (
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        sx={{ minHeight: '50vh' }}
      >
        <Grid size={6} textAlign="center">
          <CircularProgress size={40} color="inherit" />
          <Typography variant="h6" sx={{ mt: 2 }}>
            {t('app.screens.register.processingAuth')}
          </Typography>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid
      container
      justifyContent="center"
      alignItems="center"
      sx={{ minHeight: '50vh' }}
    >
      <Grid size={6} textAlign="center">
        {!error && (isRedirecting || !error) && (
          <>
            <CircularProgress size={40} color="inherit" />
            <Typography variant="h6" sx={{ mt: 2 }}>
              {isRedirecting
                ? t('app.screens.register.redirecting')
                : t('app.screens.register.loading')}
            </Typography>
          </>
        )}
        {error && (
          <Grid container direction="column" alignItems="center" spacing={2}>
            <Grid>
              <Typography variant="h6" color="error">
                {error}
              </Typography>
            </Grid>
            <Grid>
              <Button variant="contained" onClick={handleRegister} sx={{ mt: 2 }}>
                {t('app.common.retry')}
              </Button>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default RegisterScreen;
